/**
 * ChangePlanModal - Modal for changing subscription plans
 * Implements Step 15 from Change-Billing.md
 * Uses Step 11 color scheme (blue/gray, not black/white from wireframe)
 */

import { useState, useEffect, useCallback } from 'react';
import { X, Loader2, AlertCircle, CheckCircle, CreditCard } from 'lucide-react';
import { AddCardForm } from './AddCardForm';
import { DowngradeBanner } from './DowngradeBanner';

// Types
interface Plan {
  id: string;
  plan_name: string;
  plan_type: string;
  includes_mobile: boolean;
  billing_period: string;
  price: number;
}

interface BillingInfo {
  current_plan: Plan | null;
  billing_cycle_end: string | null;
  has_payment_method: boolean;
  scheduled_plan_id: string | null;
  scheduled_plan_name: string | null;
  scheduled_plan_change_date: string | null;
  cancellation_scheduled_date: string | null;
}

interface ProrationPreview {
  is_upgrade: boolean;
  is_downgrade: boolean;
  is_same_plan: boolean;
  new_plan: Plan;
  current_plan: { plan_name: string } | null;
  proration: {
    new_plan_prorated: number;   // Amount actually charged today (prorated mid-cycle)
    current_plan_credit: number; // Credit for unused time on the old plan (negative)
    total_due_today: number;
    // Service window the charge covers. Absent on the manual fallback calculation.
    charge_period_start?: string | null;
    charge_period_end?: string | null;
    // Existing account credit balance consumed by this change, if any (negative).
    account_credit_applied?: number | null;
    next_billing_date: string;
    next_billing_amount: number;
    days_prorated?: number;
    days_unused?: number;
  } | null;
  scheduled_date: string | null;
}

interface Settings {
  terms_url: string;
  refund_policy_url: string;
}

interface ChangePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  billingInfo: BillingInfo;
  onPlanChanged: () => void;
  showCloseButton?: boolean; // Default true - set to false when forcing plan selection
  isInternal?: boolean;
}

// Plan pricing data (from Change-Billing.md)
// Savings are calculated dynamically: (monthly × 12) - yearly
const PLAN_PRICES: Record<string, Record<string, { base: number; mobile: number }>> = {
  standard: {
    monthly: { base: 50, mobile: 100 },
    yearly: { base: 550, mobile: 1100 },
  },
  ai: {
    weekly: { base: 150, mobile: 165 },
    monthly: { base: 600, mobile: 600 }, // AI Monthly includes mobile
    yearly: { base: 6600, mobile: 6600 }, // AI Yearly includes mobile
  },
};

export function ChangePlanModal({
  isOpen,
  onClose,
  locationId,
  billingInfo,
  onPlanChanged,
  showCloseButton = true,
  isInternal = false,
}: ChangePlanModalProps) {
  // State
  const [planType, setPlanType] = useState<'standard' | 'ai'>('standard');
  const [billingCycle, setBillingCycle] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');
  const [includeMobile, setIncludeMobile] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);

  const [plans, setPlans] = useState<Plan[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [prorationPreview, setProrationPreview] = useState<ProrationPreview | null>(null);

  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [hasPaymentMethod, setHasPaymentMethod] = useState(billingInfo.has_payment_method);
  const [showAddCard, setShowAddCard] = useState(false);
  const [cancellingDowngrade, setCancellingDowngrade] = useState(false);
  const [cancellingCancellation, setCancellingCancellation] = useState(false);
  const [schedulingCancellation, setSchedulingCancellation] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Track if we've initialized selections from current plan
  const [hasInitializedSelections, setHasInitializedSelections] = useState(false);

  // Initialize plan selection state from current plan - ONLY once when modal opens
  // This prevents user selections from being reset when billingInfo refreshes in background
  useEffect(() => {
    if (isOpen && !hasInitializedSelections && billingInfo.current_plan) {
      setPlanType(billingInfo.current_plan.plan_type as 'standard' | 'ai');
      setBillingCycle(billingInfo.current_plan.billing_period as 'weekly' | 'monthly' | 'yearly');
      setIncludeMobile(billingInfo.current_plan.includes_mobile);
      setHasInitializedSelections(true);
    }
    // Always update payment method status (this doesn't affect user selections)
    setHasPaymentMethod(billingInfo.has_payment_method);
  }, [isOpen, billingInfo, hasInitializedSelections]);

  // Reset modal state when reopened
  useEffect(() => {
    if (isOpen) {
      // Reset success/error states for fresh interaction
      setSuccess(false);
      setError(null);
      setTermsAgreed(false);
      setShowAddCard(false);
    } else {
      // Reset initialization flag when modal closes so it re-initializes on next open
      setHasInitializedSelections(false);
    }
  }, [isOpen]);

  // Fetch plans and settings
  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [plansRes, settingsRes] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL}/billing/${locationId}/plans`, {
            credentials: 'include',
          }),
          fetch(`${import.meta.env.VITE_API_URL}/billing/${locationId}/settings`, {
            credentials: 'include',
          }),
        ]);

        const plansData = await plansRes.json() as { success: boolean; plans?: Plan[] };
        const settingsData = await settingsRes.json() as { success: boolean; settings?: Settings };

        if (plansData.success && plansData.plans) {
          setPlans(plansData.plans);
        }
        if (settingsData.success && settingsData.settings) {
          setSettings(settingsData.settings);
        }
      } catch (err) {
        console.error('[ChangePlanModal] Fetch error:', err);
        setError('Failed to load plan data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, locationId]);

  // Find selected plan from plans list
  const findSelectedPlan = useCallback((): Plan | null => {
    // Determine if mobile is included based on plan type and cycle
    const mobileIncluded = planType === 'ai' && (billingCycle === 'monthly' || billingCycle === 'yearly');
    const wantsMobile = mobileIncluded || includeMobile;

    return plans.find(
      (p) =>
        p.plan_type === planType &&
        p.billing_period === billingCycle &&
        p.includes_mobile === wantsMobile
    ) || null;
  }, [plans, planType, billingCycle, includeMobile]);

  // Fetch proration preview when selection changes
  useEffect(() => {
    if (!isOpen || plans.length === 0) return;

    const selectedPlan = findSelectedPlan();
    if (!selectedPlan) return;

    // Don't preview if same plan
    if (billingInfo.current_plan?.id === selectedPlan.id) {
      setProrationPreview(null);
      return;
    }

    const fetchPreview = async () => {
      setPreviewLoading(true);
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/billing/${locationId}/preview-plan-change`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ new_plan_id: selectedPlan.id }),
          }
        );

        const data = await res.json() as { success: boolean } & ProrationPreview;
        if (data.success) {
          setProrationPreview(data);
        }
      } catch (err) {
        console.error('[ChangePlanModal] Preview error:', err);
      } finally {
        setPreviewLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchPreview, 300); // Debounce
    return () => clearTimeout(timeoutId);
  }, [isOpen, plans, planType, billingCycle, includeMobile, locationId, billingInfo.current_plan?.id, findSelectedPlan]);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format the service window a prorated charge covers, e.g. "Aug 4, 2026 - May 11, 2027"
  const formatDateRange = (startStr?: string | null, endStr?: string | null) => {
    if (!startStr || !endStr) return null;
    const start = new Date(startStr);
    const end = new Date(endStr);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    const sameYear = start.getFullYear() === end.getFullYear();
    const startText = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
    const endText = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startText} - ${endText}`;
  };

  // Get period text
  const getPeriodText = (period: string) => {
    switch (period) {
      case 'weekly': return 'per week';
      case 'monthly': return 'per month';
      case 'yearly': return 'per year';
      default: return '';
    }
  };

  // Get selected plan price (for display)
  const getSelectedPrice = () => {
    const prices = PLAN_PRICES[planType]?.[billingCycle];
    if (!prices) return 0;

    const mobileIncluded = planType === 'ai' && (billingCycle === 'monthly' || billingCycle === 'yearly');
    if (mobileIncluded) return prices.base;

    return includeMobile ? prices.mobile : prices.base;
  };

  // Check if mobile option should be visible
  const showMobileOption = () => {
    if (planType === 'standard') return true;
    if (planType === 'ai' && billingCycle === 'weekly') return true;
    return false; // AI Monthly/Yearly includes mobile
  };

  // Check if weekly option should be visible
  const showWeeklyOption = () => {
    return planType === 'ai';
  };

  // Get savings for yearly plans (calculated dynamically based on mobile selection)
  const getYearlySavings = () => {
    const monthlyPrices = PLAN_PRICES[planType]?.monthly;
    const yearlyPrices = PLAN_PRICES[planType]?.yearly;
    if (!monthlyPrices || !yearlyPrices) return 0;

    // For AI monthly/yearly, mobile is always included
    const mobileIncluded = planType === 'ai' && (billingCycle === 'monthly' || billingCycle === 'yearly');
    const usesMobile = mobileIncluded || includeMobile;

    // Calculate: (monthly price × 12) - yearly price = savings
    const monthlyPrice = usesMobile ? monthlyPrices.mobile : monthlyPrices.base;
    const yearlyPrice = usesMobile ? yearlyPrices.mobile : yearlyPrices.base;

    return (monthlyPrice * 12) - yearlyPrice;
  };

  // Check if this is the same plan
  const isSamePlan = () => {
    if (!billingInfo.current_plan) return false;
    const selectedPlan = findSelectedPlan();
    return selectedPlan?.id === billingInfo.current_plan.id;
  };

  // Can confirm
  const canConfirm = () => {
    // Must have terms agreed
    if (!termsAgreed) return false;

    // Must have payment method
    if (!hasPaymentMethod) return false;

    // If user has current plan, must be different plan
    if (billingInfo.current_plan && isSamePlan()) return false;

    // If no current plan, any selection is valid
    return true;
  };

  // Handle plan change
  const handleConfirm = async () => {
    const selectedPlan = findSelectedPlan();
    if (!selectedPlan) {
      setError('Please select a valid plan');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/change-plan`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_plan_id: selectedPlan.id }),
        }
      );

      const data = await res.json() as { success: boolean; error?: string; message?: string };

      if (!data.success) {
        throw new Error(data.error || 'Failed to change plan');
      }

      setSuccess(true);
      setTimeout(() => {
        onPlanChanged();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('[ChangePlanModal] Change error:', err);
      setError(err instanceof Error ? err.message : 'Failed to change plan');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle card added
  const handleCardAdded = () => {
    setHasPaymentMethod(true);
    setShowAddCard(false);
  };

  // Handle cancel scheduled downgrade
  const handleCancelDowngrade = async () => {
    setCancellingDowngrade(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/cancel-downgrade`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        // Notify parent to refresh billing data
        onPlanChanged();
      } else {
        setError(data.error || 'Failed to cancel scheduled downgrade');
      }
    } catch (err) {
      console.error('[ChangePlanModal] Cancel downgrade error:', err);
      setError('Failed to cancel scheduled downgrade. Please try again.');
    } finally {
      setCancellingDowngrade(false);
    }
  };

  // Handle schedule plan cancellation
  const handleScheduleCancellation = async () => {
    setSchedulingCancellation(true);
    setError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/schedule-cancellation`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const data = await response.json() as { success: boolean; error?: string; cancellation_date?: string };

      if (data.success) {
        setShowCancelConfirm(false);
        // Notify parent to refresh billing data
        onPlanChanged();
      } else {
        setError(data.error || 'Failed to schedule plan cancellation');
      }
    } catch (err) {
      console.error('[ChangePlanModal] Schedule cancellation error:', err);
      setError('Failed to schedule plan cancellation. Please try again.');
    } finally {
      setSchedulingCancellation(false);
    }
  };

  // Handle cancel scheduled cancellation (keep plan)
  const handleCancelScheduledCancellation = async () => {
    setCancellingCancellation(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/cancel-scheduled-cancellation`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        // Notify parent to refresh billing data
        onPlanChanged();
      } else {
        setError(data.error || 'Failed to cancel scheduled cancellation');
      }
    } catch (err) {
      console.error('[ChangePlanModal] Cancel scheduled cancellation error:', err);
      setError('Failed to keep plan. Please try again.');
    } finally {
      setCancellingCancellation(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header - stays fixed at top */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {showCloseButton ? 'Change Your Plan' : 'Select a Plan to Continue'}
          </h2>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : success ? (
          <div className="p-12 flex flex-col items-center justify-center">
            <CheckCircle className="w-16 h-16 text-green-600 mb-4" />
            <p className="text-xl font-semibold text-gray-900">Plan Changed Successfully!</p>
            <p className="text-gray-600 mt-2">Your new plan is now active.</p>
          </div>
        ) : (
          <div className="p-6">
            {/* Scheduled Downgrade Banner (top of modal) */}
            {billingInfo.scheduled_plan_id && billingInfo.scheduled_plan_name && billingInfo.scheduled_plan_change_date && (
              <DowngradeBanner
                variant="modal"
                type="downgrade"
                scheduledPlanName={billingInfo.scheduled_plan_name}
                scheduledDate={billingInfo.scheduled_plan_change_date}
                onCancel={handleCancelDowngrade}
                cancelling={cancellingDowngrade}
              />
            )}

            {/* Scheduled Cancellation Banner (top of modal) */}
            {billingInfo.cancellation_scheduled_date && !billingInfo.scheduled_plan_id && (
              <DowngradeBanner
                variant="modal"
                type="cancellation"
                scheduledDate={billingInfo.cancellation_scheduled_date}
                onCancel={handleCancelScheduledCancellation}
                cancelling={cancellingCancellation}
              />
            )}

            {/* Internal User Note */}
            {isInternal && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-purple-800">
                  <span className="font-medium">Note:</span> As an internal user, a subscription plan is not required to access services. Your account bypasses plan requirements.
                </p>
              </div>
            )}

            {/* Current Plan Section */}
            <div className="bg-gray-100 rounded-lg p-5 mb-6 text-center">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Your Current Plan
              </div>
              {billingInfo.current_plan ? (
                <>
                  <div className="text-xl font-bold text-gray-900">
                    {billingInfo.current_plan.plan_name}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Next invoice: {formatDate(billingInfo.billing_cycle_end)}
                  </div>
                </>
              ) : (
                <div className="text-xl font-bold text-gray-500">No Current Plan</div>
              )}

              {/* Scheduled Downgrade Notice */}
              {billingInfo.scheduled_plan_id && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-sm text-amber-800">
                    <span className="font-medium">Scheduled change:</span> Your plan will change to{' '}
                    <span className="font-semibold">{billingInfo.scheduled_plan_name}</span> on{' '}
                    {formatDate(billingInfo.scheduled_plan_change_date)}
                  </p>
                </div>
              )}
            </div>

            {/* Plan Type Selection */}
            <div className="mb-6">
              <div className="text-center text-lg font-semibold text-gray-900 mb-4">
                Compare Plans
              </div>
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => setPlanType('standard')}
                  className={`px-8 py-3 rounded-md font-medium transition-colors ${
                    planType === 'standard'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Standard
                </button>
                <button
                  onClick={() => setPlanType('ai')}
                  className={`px-8 py-3 rounded-md font-medium transition-colors ${
                    planType === 'ai'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  AI
                </button>
              </div>
            </div>

            {/* Billing Cycle Selection */}
            <div className="mb-6 text-center">
              <div className="text-sm font-medium text-gray-700 mb-3">Billing Cycle</div>
              <div className="flex justify-center gap-3">
                {showWeeklyOption() && (
                  <button
                    onClick={() => setBillingCycle('weekly')}
                    className={`px-6 py-2.5 rounded-md font-medium transition-colors ${
                      billingCycle === 'weekly'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Weekly
                  </button>
                )}
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2.5 rounded-md font-medium transition-colors ${
                    billingCycle === 'monthly'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`px-6 py-2.5 rounded-md font-medium transition-colors ${
                    billingCycle === 'yearly'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Yearly
                  {getYearlySavings() > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded">
                      Save {formatCurrency(getYearlySavings())}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Mobile App Checkbox */}
            {showMobileOption() && (
              <div className="mb-6 flex justify-center">
                <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={includeMobile}
                    onChange={(e) => setIncludeMobile(e.target.checked)}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="font-medium text-gray-700">Add Mobile App</span>
                </label>
              </div>
            )}

            {/* AI Monthly/Yearly Mobile Included Notice */}
            {planType === 'ai' && (billingCycle === 'monthly' || billingCycle === 'yearly') && (
              <div className="mb-6 text-center text-sm text-green-600 font-medium">
                Mobile App included with AI {billingCycle === 'monthly' ? 'Monthly' : 'Yearly'} plan
              </div>
            )}

            {/* Plan Comparison Cards */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* Current Plan Card */}
              <div className="border border-gray-300 rounded-lg p-6 bg-gray-50 text-center">
                <div className="inline-block px-3 py-1 bg-gray-700 text-white text-xs font-semibold rounded mb-4">
                  CURRENT PLAN
                </div>
                {billingInfo.current_plan ? (
                  <>
                    <div className="text-lg font-bold text-gray-700">
                      {billingInfo.current_plan.plan_name.replace(/ \([^)]+\)$/, '')}
                    </div>
                    <div className="text-3xl font-bold text-gray-700 mt-2">
                      {formatCurrency(billingInfo.current_plan.price)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {getPeriodText(billingInfo.current_plan.billing_period)}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-lg font-bold text-gray-500">No Current Plan</div>
                    <div className="text-3xl font-bold text-gray-400 mt-2">$0</div>
                    <div className="text-sm text-gray-400">per month</div>
                  </>
                )}
              </div>

              {/* New Plan Card */}
              <div className={`border-2 rounded-lg p-6 text-center ${
                isSamePlan() ? 'border-gray-300 bg-gray-50' : 'border-blue-500 bg-blue-50'
              }`}>
                <div className={`inline-block px-3 py-1 text-white text-xs font-semibold rounded mb-4 ${
                  isSamePlan() ? 'bg-gray-500' : 'bg-blue-600'
                }`}>
                  NEW PLAN
                </div>
                <div className={`text-lg font-bold ${isSamePlan() ? 'text-gray-700' : 'text-blue-900'}`}>
                  {findSelectedPlan()?.plan_name || `${planType === 'standard' ? 'Standard' : 'AI'}${(showMobileOption() && includeMobile) || (!showMobileOption()) ? ' + Mobile' : ''}`}
                </div>
                <div className={`text-3xl font-bold mt-2 ${isSamePlan() ? 'text-gray-700' : 'text-blue-900'}`}>
                  {formatCurrency(getSelectedPrice())}
                </div>
                <div className={`text-sm ${isSamePlan() ? 'text-gray-500' : 'text-blue-700'}`}>
                  {getPeriodText(billingCycle)}
                </div>
                {billingCycle === 'yearly' && getYearlySavings() > 0 && !isSamePlan() && (
                  <div className="mt-2 inline-block px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded">
                    SAVE {formatCurrency(getYearlySavings())}
                  </div>
                )}
              </div>
            </div>

            {/* No Payment Method Warning */}
            {!hasPaymentMethod && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start mb-3">
                  <CreditCard className="w-5 h-5 text-amber-600 mt-0.5 mr-2" />
                  <div>
                    <p className="font-medium text-amber-800">Payment method required</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Please add a payment method to change your plan.
                    </p>
                  </div>
                </div>
                {showAddCard ? (
                  <AddCardForm
                    locationId={locationId}
                    onSuccess={handleCardAdded}
                    onCancel={() => setShowAddCard(false)}
                    setAsPrimarySubscription={true}
                    setAsPrimaryTopup={false}  // Only set wallet primary if no cards exist (first card rule)
                  />
                ) : (
                  <button
                    onClick={() => setShowAddCard(true)}
                    className="w-full py-2.5 px-4 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Add Payment Method
                  </button>
                )}
              </div>
            )}

            {/* Order Summary */}
            <div className="bg-gray-100 rounded-lg p-5 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">New Plan:</span>
                  <span className="font-medium text-gray-900">
                    {findSelectedPlan()?.plan_name || `${planType === 'standard' ? 'Standard' : 'AI'}${(showMobileOption() && includeMobile) || (!showMobileOption()) ? ' + Mobile' : ''}`}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Billing Cycle:</span>
                  <span className="font-medium text-gray-900">
                    {billingCycle.charAt(0).toUpperCase() + billingCycle.slice(1)}
                  </span>
                </div>
              </div>

              {/* Proration Details */}
              {previewLoading ? (
                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600 mr-2" />
                  <span className="text-sm text-gray-600">Calculating...</span>
                </div>
              ) : prorationPreview?.proration && !isSamePlan() ? (
                <div className="mt-4 pt-4 border-t border-dashed border-gray-300 space-y-2">
                  <div className="flex justify-between text-sm gap-4">
                    <span className="text-gray-500">
                      {prorationPreview.new_plan.plan_name}
                      {formatDateRange(
                        prorationPreview.proration.charge_period_start,
                        prorationPreview.proration.charge_period_end
                      ) && (
                        <span className="block text-xs text-gray-400">
                          {formatDateRange(
                            prorationPreview.proration.charge_period_start,
                            prorationPreview.proration.charge_period_end
                          )}
                        </span>
                      )}
                    </span>
                    <span className="text-gray-700 whitespace-nowrap">
                      {formatCurrency(prorationPreview.proration.new_plan_prorated)}
                    </span>
                  </div>
                  {prorationPreview.proration.current_plan_credit < 0 && (
                    <div className="flex justify-between text-sm gap-4">
                      <span className="text-green-600">
                        {prorationPreview.current_plan
                          ? `Credit for unused ${prorationPreview.current_plan.plan_name}`
                          : 'Credit for unused time on current plan'}
                      </span>
                      <span className="text-green-600 whitespace-nowrap">
                        {formatCurrency(prorationPreview.proration.current_plan_credit)}
                      </span>
                    </div>
                  )}
                  {(prorationPreview.proration.account_credit_applied ?? 0) < 0 && (
                    <div className="flex justify-between text-sm gap-4">
                      <span className="text-green-600">
                        Account credit applied
                      </span>
                      <span className="text-green-600 whitespace-nowrap">
                        {formatCurrency(prorationPreview.proration.account_credit_applied ?? 0)}
                      </span>
                    </div>
                  )}
                </div>
              ) : prorationPreview?.is_downgrade && !isSamePlan() ? (
                <div className="mt-4 pt-4 border-t border-dashed border-gray-300">
                  <div className="flex items-center text-sm text-amber-700 bg-amber-50 p-3 rounded">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    <span>
                      This downgrade will take effect on {formatDate(prorationPreview.scheduled_date)}
                    </span>
                  </div>
                </div>
              ) : null}

              {/* Total */}
              {!isSamePlan() && prorationPreview && (
                <div className="mt-4 pt-4 border-t-2 border-gray-300">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900">Total Due Today:</span>
                    <span className="text-lg font-bold text-gray-900">
                      {prorationPreview.proration
                        ? formatCurrency(prorationPreview.proration.total_due_today)
                        : '$0.00'}
                    </span>
                  </div>
                  {prorationPreview.proration && (
                    <p className="text-sm text-gray-500 mt-2">
                      Then {formatCurrency(prorationPreview.proration.next_billing_amount)}
                      {getPeriodText(billingCycle).replace('per ', '/')} starting{' '}
                      {formatDate(prorationPreview.proration.next_billing_date)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Terms Checkbox */}
            <div className="mb-4 p-4 border-t border-gray-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAgreed}
                  onChange={(e) => setTermsAgreed(e.target.checked)}
                  className="w-5 h-5 mt-0.5 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  I agree to the{' '}
                  <a
                    href={settings?.terms_url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Terms & Conditions
                  </a>{' '}
                  and{' '}
                  <a
                    href={settings?.refund_policy_url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Refund Policy
                  </a>
                </span>
              </label>
            </div>

            {/* Billing Policy Notice */}
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="font-semibold text-amber-900 mb-2">Billing Policy:</div>
              <div className="text-sm text-amber-800 space-y-1">
                <p>
                  <span className="font-medium">Upgrades:</span> Take effect immediately. You'll be
                  charged the new plan price minus prorated credit from your current plan.
                </p>
                <p>
                  <span className="font-medium">Downgrades:</span> Take effect at the end of your
                  current billing cycle. You'll retain access to current features until then.
                </p>
              </div>
            </div>

            {/* Payment Authorization */}
            <p className="text-xs text-gray-500 text-center mb-4">
              By confirming, you authorize us to charge your payment method for the amount shown
              above. Recurring charges will apply based on your selected billing cycle.
            </p>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Confirm Button */}
            <button
              onClick={handleConfirm}
              disabled={!canConfirm() || submitting}
              className={`w-full py-3.5 px-4 rounded-md text-white font-semibold transition-colors flex items-center justify-center ${
                canConfirm() && !submitting
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : isSamePlan() ? (
                'Select a Different Plan'
              ) : (
                'Confirm Plan Change'
              )}
            </button>

            {/* Cancel Plan Link - only show if user has an active plan and no cancellation is already scheduled */}
            {billingInfo.current_plan && !billingInfo.cancellation_scheduled_date && !billingInfo.scheduled_plan_id && (
              <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                {showCancelConfirm ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800 mb-3">
                      Are you sure you want to cancel your plan? Your access will continue until the end of your current billing cycle.
                    </p>
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={() => setShowCancelConfirm(false)}
                        disabled={schedulingCancellation}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                      >
                        Never mind
                      </button>
                      <button
                        onClick={handleScheduleCancellation}
                        disabled={schedulingCancellation}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
                      >
                        {schedulingCancellation ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Cancelling...
                          </>
                        ) : (
                          'Yes, cancel my plan'
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="text-sm text-gray-500 hover:text-red-600 transition-colors"
                  >
                    Cancel my plan
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
